import { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Gauge } from 'lucide-react';
import { METRICS, type MetricKey } from '@raptor/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { LoadingState } from '@/components/LoadingState';
import { useLatestReadings, useWells } from '../api';
import { ChartsSection } from './ChartsSection';
import { ExportSection } from './ExportSection';
import type { CustomerWell, WellLatestReading } from '../types';

const ALL = 'all';

// Only charted metrics appear on the dashboard — qAnalytics (charted:false) is
// stored but never shown here.
const CHARTED_METRICS = (Object.keys(METRICS) as MetricKey[]).filter((k) => METRICS[k].charted);

interface FlatWell extends CustomerWell {
  siteId: string;
  siteName: string;
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  testId: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">{label}:</span>
      <div className="w-48">
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger data-testid={testId}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function MetricValue({
  metric,
  reading,
}: {
  metric: MetricKey;
  reading: WellLatestReading | undefined;
}) {
  const cfg = METRICS[metric];
  const raw = reading ? (reading[metric] as number | null | undefined) : undefined;
  const display = typeof raw === 'number' ? `${raw.toFixed(cfg.precision)} ${cfg.unit}` : '—';
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{cfg.label}</div>
      <div className="text-xl font-semibold tabular-nums">{display}</div>
    </div>
  );
}

function WellCard({ well, reading }: { well: FlatWell; reading: WellLatestReading | undefined }) {
  return (
    <Card data-testid={`well-summary-${well.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="truncate text-base">{well.name}</CardTitle>
            <code className="text-xs text-muted-foreground">{well.wellCode}</code>
          </div>
          <StatusBadge
            variant={well.isActive ? 'success' : 'neutral'}
            label={well.isActive ? 'Active' : 'Inactive'}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {CHARTED_METRICS.map((metric) => (
            <MetricValue key={metric} metric={metric} reading={reading} />
          ))}
        </div>
        <div className="text-xs text-muted-foreground" data-testid={`well-summary-time-${well.id}`}>
          {reading
            ? `Last reported ${formatDistanceToNow(new Date(reading.recordedAt), { addSuffix: true })}`
            : 'Never reported'}
        </div>
      </CardContent>
    </Card>
  );
}

export function WellsDashboard() {
  const wells = useWells();
  const latest = useLatestReadings();
  const [siteId, setSiteId] = useState<string>(ALL);
  const [wellId, setWellId] = useState<string>(ALL);

  const flatWells = useMemo<FlatWell[]>(() => {
    const out: FlatWell[] = [];
    for (const site of wells.data?.sites ?? []) {
      for (const w of site.wells) {
        out.push({ ...w, siteId: site.id, siteName: site.name });
      }
    }
    return out;
  }, [wells.data]);

  const latestByWell = useMemo(() => {
    const map = new Map<string, WellLatestReading>();
    for (const r of latest.data?.readings ?? []) map.set(r.wellId, r);
    return map;
  }, [latest.data]);

  // Well selector narrows to the chosen site.
  const wellsForSite = siteId === ALL ? flatWells : flatWells.filter((w) => w.siteId === siteId);
  const visibleWells = wellId === ALL ? wellsForSite : wellsForSite.filter((w) => w.id === wellId);

  const siteOptions = [
    { value: ALL, label: 'All sites' },
    ...(wells.data?.sites ?? []).map((s) => ({ value: s.id, label: s.name })),
  ];
  const wellOptions = [
    { value: ALL, label: 'All wells' },
    ...wellsForSite.map((w) => ({ value: w.id, label: w.name })),
  ];

  if (wells.isLoading) {
    return <LoadingState rows={4} />;
  }

  if (wells.isError) {
    return (
      <EmptyState
        Icon={Gauge}
        title="Couldn't load your wells"
        description="Something went wrong fetching your telemetry."
        action={
          <Button variant="outline" size="sm" onClick={() => void wells.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  if (flatWells.length === 0) {
    return (
      <EmptyState
        Icon={Gauge}
        title="No wells yet"
        description="No wells have been set up for your account yet — contact your administrator."
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="wells-dashboard">
      <div className="flex flex-wrap items-center gap-4">
        <FilterSelect
          label="Site"
          testId="wells-filter-site"
          value={siteId}
          onChange={(v) => {
            setSiteId(v);
            setWellId(ALL); // reset well when the site (and its well list) changes
          }}
          options={siteOptions}
        />
        <FilterSelect
          label="Well"
          testId="wells-filter-well"
          value={wellId}
          onChange={setWellId}
          options={wellOptions}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleWells.map((well) => (
          <WellCard key={well.id} well={well} reading={latestByWell.get(well.id)} />
        ))}
      </div>

      {/* Charts region below the latest-reading summary. */}
      <ChartsSection wells={flatWells.map((w) => ({ id: w.id, name: w.name }))} />

      {/* CSV export region. */}
      <ExportSection wells={flatWells.map((w) => ({ id: w.id, name: w.name }))} />
    </div>
  );
}
